/**
 * Templates as design tokens — **not** per-component stylesheets.
 *
 * A template is a set of tokens (palette, type, radius, density), and a site is
 * `template + per-block variant`. Five templates do not mean five copies of every block: every block
 * reads the same role tokens, so a fourteenth block works under every template on the day it ships,
 * and a sixth template works with every block on the day *it* ships. This is the single decision
 * that keeps the block count and the template count from multiplying.
 *
 * ## Role pairs
 *
 * Colours are declared as PAIRS — `brand`/`on-brand`, `surface`/`on-surface`, `accent`/`on-accent`.
 * The `on-` colour is the text that sits on its partner, so contrast is *structural* rather than
 * something a customer has to check. A role with no partner (`muted`, `border`) is not a background,
 * and `GROUND_ROLES` in `@ksa/model` is what enforces that at the point a ground is chosen.
 *
 * ## ⚠️ The contrast gate reads THIS FILE, by importing it
 *
 * `scripts/check-contrast.ts` imports `TEMPLATES` and `OPACITY` directly and fails the build below
 * WCAG AA. The predecessor's equivalent *parsed the source with regular expressions*, and its own
 * documentation recorded the two ways that could go quiet — a table it could not parse, and a role
 * list it could not parse, both of which had to be made errors rather than clean reports. Importing
 * the module removes that entire class of failure: if the table cannot be read, the script cannot
 * start.
 *
 * ## ⚠️ Opacity is declared here, not written into components
 *
 * `OPACITY.soft` composites text toward its ground, which is exactly what a table of hexes cannot
 * see. In the predecessor, a static check passed and the browser sweep then caught a softened
 * subheading at 3.86:1 — the static check was not testing the composited pair. Here the components
 * import these constants and the checker imports the same ones, so a fifth softened element cannot
 * appear without the gate knowing about it.
 */

import type { SiteRole } from '@ksa/model'
import type { Text } from '@ksa/model'

/**
 * Every opacity the renderer is allowed to apply to text, and — just as load-bearing — the role each
 * one may be applied TO. Adding an entry here is what tells the contrast gate to start checking it;
 * applying an opacity that is not in this table is the bug.
 *
 * ⚠️ `on`, NOT JUST A NUMBER, AND THE GATE FOUND OUT WHY. `faint` was first declared as a bare 0.75
 * and applied to `muted`, which measured **3.51:1 to 3.85:1 on all five templates** — below AA on
 * every one. The mistake is visible once it is named: `muted` is *already* the quiet colour, so
 * softening it is quieting the same text twice. Opacity may only ever be applied to an `on-` role,
 * which by construction has contrast to spare against its partner.
 */
export const OPACITY = {
  /** A supporting line under a headline. Applied to whichever `on-` colour the ground pairs with. */
  soft: { alpha: 0.85, on: 'ground' },
  /** A caption or small print. `surface` only — never on a chosen ground, where the headroom above
   *  AA is what the softening spends. */
  faint: { alpha: 0.75, on: 'surface' },
} as const

export type Template = {
  id: string
  label: Text
  /** One line the picker shows, so a customer chooses by feel rather than by name. */
  mood: Text
  colors: Record<SiteRole, string>
  /**
   * > ⚠️ **THIS SAID THE STACK IS A SYSTEM STACK AND NAMED THE CONDITION THAT WOULD END IT — "the
   * > moment a template needs a voice a system font cannot give it". THAT EXPIRY HAS ARRIVED, AND
   * > NOT FOR THAT REASON.** The stacks' FIRST entry is `'IBM Plex Sans Arabic'`, a font the BUILDER
   * > loads from `@fontsource` and a published page did not — so the editor's preview resolved to it
   * > and the live site fell through to `'Noto Sans Arabic'`. Two different-looking sites, and the
   * > one the customer approved was the one nobody else could see.
   * >
   * > The rest of the reasoning stands and is why the answer is shaped the way it is: a webfont is
   * > 80–200 kB before the first word is legible, the Arabic half is the larger one, and on a Saudi
   * > mobile connection that is the difference between instant and flashing.
   *
   * The font is now published WITH the site — subsetted, self-hosted, `font-display: swap`, exactly
   * as the sentence above prescribed and never a Google Fonts link, because a published customer site
   * that reaches a third party on load is a third party that sees every one of their visitors. See
   * `fonts.ts` for what it costs and the three things that make it payable.
   *
   * ⚠️ THE STACKS ARE STILL SYSTEM STACKS AND MUST STAY SO. They are what `font-display: swap` falls
   * back to, which is what keeps the page as fast to readable text as it was before — so every entry
   * after the first still has to be a font that ships with Windows, macOS, iOS and Android and that
   * carries BOTH scripts.
   */
  font: string
  headingFont?: string
  radius: string
  /** Section padding scale. `density` is what makes `ink` feel spacious and `bold` feel urgent
   *  without either of them owning a stylesheet. */
  density: 'tight' | 'normal' | 'roomy'
}

/**
 * The stacks. IBM Plex Sans Arabic and Noto Sans Arabic are named first where available because they
 * are the two faces that render Arabic and Latin at matching weights — a page that pairs a Latin
 * face with a fallback Arabic one has two different-looking languages on it.
 */
const SANS =
  "'IBM Plex Sans Arabic', 'Noto Sans Arabic', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif"
const SERIF = "'Noto Naskh Arabic', 'Times New Roman', Georgia, serif"

export const TEMPLATES: Record<string, Template> = {
  warm: {
    id: 'warm',
    label: { en: 'Warm', ar: 'دافئ' },
    mood: { en: 'Earthy and inviting — food, hospitality, crafts.', ar: 'ترابي ومرحّب — طعام وضيافة وحِرف.' },
    colors: {
      brand: '#7a2f14',
      'on-brand': '#fdf6f0',
      surface: '#fdf8f3',
      'on-surface': '#2a211c',
      muted: '#66584f',
      accent: '#93502a',
      'on-accent': '#fffaf7',
      border: '#e7dccf',
    },
    font: SANS,
    radius: '14px',
    density: 'normal',
  },
  cool: {
    id: 'cool',
    label: { en: 'Cool', ar: 'بارد' },
    mood: { en: 'Calm and professional — services, consultancies, agencies.', ar: 'هادئ ومهني — خدمات واستشارات ووكالات.' },
    colors: {
      brand: '#0d4d5c',
      'on-brand': '#eff8fa',
      surface: '#f6f9fa',
      'on-surface': '#14222a',
      muted: '#4f6167',
      accent: '#1a6577',
      'on-accent': '#fafeff',
      border: '#d5e0e5',
    },
    font: SANS,
    radius: '10px',
    density: 'normal',
  },
  bold: {
    id: 'bold',
    label: { en: 'Bold', ar: 'جريء' },
    mood: { en: 'High contrast and loud — shops, launches, offers.', ar: 'تباين عالٍ وصوت واضح — متاجر وإطلاقات وعروض.' },
    colors: {
      brand: '#131417',
      'on-brand': '#f7f7f8',
      surface: '#ffffff',
      'on-surface': '#17181c',
      muted: '#55585f',
      accent: '#a81b41',
      'on-accent': '#ffffff',
      border: '#e3e4e8',
    },
    font: SANS,
    radius: '4px',
    density: 'tight',
  },
  calm: {
    id: 'calm',
    label: { en: 'Calm', ar: 'مطمئن' },
    mood: { en: 'Quiet and trustworthy — clinics, care, education.', ar: 'هادئ وموثوق — عيادات ورعاية وتعليم.' },
    colors: {
      brand: '#14524a',
      'on-brand': '#f0faf7',
      surface: '#f7faf9',
      'on-surface': '#16241f',
      muted: '#4e615a',
      accent: '#1a655a',
      'on-accent': '#f8fffd',
      border: '#d8e5e0',
    },
    font: SANS,
    radius: '18px',
    density: 'roomy',
  },
  ink: {
    id: 'ink',
    label: { en: 'Ink', ar: 'حبر' },
    mood: { en: 'Editorial and spare — portfolios, studios, writing.', ar: 'تحريري ومقتصد — أعمال واستوديوهات وكتابة.' },
    colors: {
      brand: '#1c1c1e',
      'on-brand': '#f5f5f4',
      surface: '#fafaf9',
      'on-surface': '#1c1c1e',
      muted: '#57574f',
      accent: '#3a3a42',
      'on-accent': '#fafaf9',
      border: '#e4e4e1',
    },
    font: SANS,
    headingFont: SERIF,
    radius: '2px',
    density: 'roomy',
  },
}

export const TEMPLATE_IDS = Object.keys(TEMPLATES)

/** Falls back rather than throwing. A document naming a template that has been retired must still
 *  render — a published site whose theme was removed is not a site that should go blank. */
export function getTemplate(id: string): Template {
  return TEMPLATES[id] ?? TEMPLATES.warm!
}

const PAD: Record<Template['density'], string> = {
  tight: '2.5rem',
  normal: '3.5rem',
  roomy: '5rem',
}

/** The CSS custom properties for a template. Applied to the preview root in the editor and to
 *  `<body>` in a published page — the same declaration, which is what makes the preview honest. */
export function templateVars(template: Template): Record<string, string> {
  const vars: Record<string, string> = {
    '--site-font': template.font,
    '--site-heading-font': template.headingFont ?? template.font,
    '--site-radius': template.radius,
    '--site-pad': PAD[template.density],
    '--site-soft': String(OPACITY.soft.alpha),
    '--site-faint': String(OPACITY.faint.alpha),
  }
  for (const [role, value] of Object.entries(template.colors)) vars[`--${role}`] = value
  return vars
}

/** Reference a role token in a style value: `roleVar('brand')` → `var(--brand)`. */
export function roleVar(role: SiteRole): string {
  return `var(--${role})`
}
